// Minimal browser-side MCP (Streamable HTTP) client + bridge to pi-agent-core's AgentTool.
// Talks to a remote MCP server over JSON-RPC. The server may respond with
// either `application/json` or `text/event-stream` (SSE-framed) — we handle both.
//
// Wires THREE MCP surfaces:
//   1. Server `instructions` — captured from initialize and returned so the host
//      can inject them into the agent's system prompt (Claude-Desktop-style).
//   2. Tools — each remote tool becomes a pi-agent-core AgentTool.
//   3. Resources — exposed via a synthetic `read_mcp_resource(uri)` AgentTool
//      that calls `resources/read` on demand. Plus an optional resource list
//      can be appended to the system prompt so the model knows what URIs exist.

import type { AgentTool, AgentToolResult } from "@earendil-works/pi-agent-core";
import { Type, type TSchema } from "typebox";

interface JsonRpcSuccess {
	jsonrpc: "2.0";
	id?: number | string | null;
	result: any;
}
interface JsonRpcError {
	jsonrpc: "2.0";
	id?: number | string | null;
	error: { code: number; message: string; data?: any };
}
type JsonRpcResponse = JsonRpcSuccess | JsonRpcError;

interface McpToolSchema {
	name: string;
	description: string;
	inputSchema: {
		type: "object";
		properties?: Record<string, any>;
		required?: string[];
		additionalProperties?: boolean;
	};
}

export interface McpResourceMeta {
	uri: string;
	name?: string;
	description?: string;
	mimeType?: string;
}

export interface McpResourceContent {
	uri: string;
	mimeType?: string;
	text?: string;
	blob?: string;
}

export interface McpClientOptions {
	url: string;
	bearerToken?: string;
	clientName?: string;
	clientVersion?: string;
}

export class McpClient {
	private url: string;
	private bearerToken?: string;
	private clientName: string;
	private clientVersion: string;
	private sessionId?: string;
	private nextId = 1;
	private _instructions = "";
	private _serverInfo: { name?: string; version?: string } = {};
	private _capabilities: Record<string, any> = {};

	constructor(opts: McpClientOptions) {
		this.url = opts.url;
		this.bearerToken = opts.bearerToken;
		this.clientName = opts.clientName ?? "myjarvis-web";
		this.clientVersion = opts.clientVersion ?? "0.1.0";
	}

	get instructions(): string {
		return this._instructions;
	}
	get serverInfo() {
		return this._serverInfo;
	}
	get capabilities() {
		return this._capabilities;
	}

	private headers(extra: Record<string, string> = {}): Record<string, string> {
		const h: Record<string, string> = {
			"Content-Type": "application/json",
			Accept: "application/json, text/event-stream",
			...extra,
		};
		if (this.bearerToken) h.Authorization = `Bearer ${this.bearerToken}`;
		if (this.sessionId) h["Mcp-Session-Id"] = this.sessionId;
		return h;
	}

	private async rpc(method: string, params?: any, isNotification = false): Promise<JsonRpcResponse | null> {
		const body: any = { jsonrpc: "2.0", method };
		if (params !== undefined) body.params = params;
		if (!isNotification) body.id = this.nextId++;

		const resp = await fetch(this.url, {
			method: "POST",
			headers: this.headers(),
			body: JSON.stringify(body),
		});
		if (!resp.ok && resp.status !== 202) {
			const text = await resp.text();
			throw new Error(`MCP ${method} failed: ${resp.status} ${text.slice(0, 300)}`);
		}

		if (method === "initialize") {
			const sid = resp.headers.get("mcp-session-id");
			if (sid) this.sessionId = sid;
		}

		if (isNotification) return null;

		const contentType = resp.headers.get("content-type") || "";
		const text = await resp.text();
		if (contentType.includes("text/event-stream")) {
			const line = text.split("\n").find((l) => l.startsWith("data:"));
			if (!line) throw new Error(`MCP ${method}: no data: line in SSE response`);
			return JSON.parse(line.slice(5).trim());
		}
		return JSON.parse(text);
	}

	async connect(): Promise<void> {
		const initResp = await this.rpc("initialize", {
			protocolVersion: "2025-03-26",
			capabilities: {},
			clientInfo: { name: this.clientName, version: this.clientVersion },
		});
		if (!initResp || "error" in initResp) {
			throw new Error(`MCP initialize failed: ${JSON.stringify(initResp)}`);
		}
		const result = initResp.result ?? {};
		this._instructions = typeof result.instructions === "string" ? result.instructions : "";
		this._serverInfo = result.serverInfo ?? {};
		this._capabilities = result.capabilities ?? {};
		await this.rpc("notifications/initialized", undefined, true);
	}

	async listTools(): Promise<McpToolSchema[]> {
		const resp = await this.rpc("tools/list");
		if (!resp || "error" in resp) throw new Error(`tools/list failed: ${JSON.stringify(resp)}`);
		return resp.result.tools as McpToolSchema[];
	}

	async callTool(name: string, args: Record<string, any>): Promise<{ content: any[]; isError?: boolean }> {
		const resp = await this.rpc("tools/call", { name, arguments: args });
		if (!resp || "error" in resp) {
			const msg = "error" in (resp || {}) ? (resp as JsonRpcError).error.message : "unknown";
			return { isError: true, content: [{ type: "text", text: `MCP error: ${msg}` }] };
		}
		return resp.result;
	}

	async listResources(): Promise<McpResourceMeta[]> {
		if (!this._capabilities?.resources) return [];
		const resp = await this.rpc("resources/list");
		if (!resp || "error" in resp) return [];
		return (resp.result?.resources ?? []) as McpResourceMeta[];
	}

	async readResource(uri: string): Promise<{ contents: McpResourceContent[] }> {
		const resp = await this.rpc("resources/read", { uri });
		if (!resp || "error" in resp) {
			const msg = "error" in (resp || {}) ? (resp as JsonRpcError).error.message : "unknown";
			throw new Error(`resources/read failed for ${uri}: ${msg}`);
		}
		return resp.result;
	}
}

function jsonSchemaToTypeBox(schema: any): TSchema {
	if (!schema || typeof schema !== "object") return Type.Object({}, { additionalProperties: true });
	return Type.Unsafe<any>(schema);
}

export interface CreateMcpToolsOptions {
	url: string;
	bearerToken?: string;
	include?: string[];
	prefix?: string;
	labelPrefix?: string;
}

export interface McpBridge {
	tools: AgentTool[];
	instructions: string;
	resources: McpResourceMeta[];
	serverInfo: { name?: string; version?: string };
}

export async function createMcpAgentTools(opts: CreateMcpToolsOptions): Promise<McpBridge> {
	const client = new McpClient({ url: opts.url, bearerToken: opts.bearerToken });
	await client.connect();
	const tools = await client.listTools();
	const resources = await client.listResources();
	const prefix = opts.prefix ?? "";
	const labelPrefix = opts.labelPrefix ?? "";
	const filtered = opts.include ? tools.filter((t) => opts.include!.includes(t.name)) : tools;

	const wrappedTools: AgentTool[] = filtered.map((mcpTool): AgentTool => {
		const name = `${prefix}${mcpTool.name}`;
		return {
			name,
			label: `${labelPrefix}${mcpTool.name}`,
			description: mcpTool.description,
			parameters: jsonSchemaToTypeBox(mcpTool.inputSchema),
			execute: async (_toolCallId: string, params: any): Promise<AgentToolResult<any>> => {
				const result = await client.callTool(mcpTool.name, params || {});
				const textBlocks = (result.content || [])
					.filter((c: any) => c?.type === "text")
					.map((c: any) => ({ type: "text" as const, text: String(c.text ?? "") }));
				return {
					content: textBlocks.length > 0 ? textBlocks : [{ type: "text", text: JSON.stringify(result) }],
					details: result,
				};
			},
		};
	});

	if (client.capabilities?.resources) {
		wrappedTools.push({
			name: `${prefix}read_mcp_resource`,
			label: `${labelPrefix}read_mcp_resource`,
			description:
				"Fetch the contents of an MCP resource by URI (for example `skill://dashboard`). " +
				"Use this whenever the user or the instructions reference a resource URI — do NOT try `read_file` for non-repo URIs.",
			parameters: Type.Object({
				uri: Type.String({
					description: "The MCP resource URI to read, e.g. `skill://dashboard`.",
				}),
			}),
			execute: async (_toolCallId: string, params: any): Promise<AgentToolResult<any>> => {
				const uri = String(params?.uri ?? "");
				if (!uri) {
					return {
						content: [{ type: "text", text: "read_mcp_resource error: missing `uri` argument." }],
						details: { error: "missing_uri" },
					};
				}
				try {
					const result = await client.readResource(uri);
					const textBlocks = (result.contents || [])
						.filter((c) => typeof c.text === "string")
						.map((c) => ({ type: "text" as const, text: c.text ?? "" }));
					return {
						content: textBlocks.length > 0 ? textBlocks : [{ type: "text", text: JSON.stringify(result) }],
						details: result,
					};
				} catch (err) {
					return {
						content: [{ type: "text", text: `read_mcp_resource error: ${err instanceof Error ? err.message : String(err)}` }],
						details: { error: String(err) },
					};
				}
			},
		});
	}

	return {
		tools: wrappedTools,
		instructions: client.instructions,
		resources,
		serverInfo: client.serverInfo,
	};
}
